import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, NavLink, useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import {
  Table,
  useQueryState,
  Filters,
  useFilters,
  BaseTransaction,
  useGetList,
  usePrevious,
  IconExpand,
  ButtonSize,
  ButtonPrimary,
  PermissionEnum,
  SchemaFilterType,
  BaseellaStatus,
  ButtonSecondary,
  SignaturePopup,
  ButtonPseudo,
  IconDownload,
  SelectItemProps,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  useBatchCreateTransactionSignatureMutation,
  useSendCodeMutation,
  useValidateCodeMutation,
} from '../../store/signature/service';
import { MfaToken, MfaType } from '../../store/types';
import {
  clearOutgoingListState,
  setExpandedTransactionId,
  setSelectedCount,
} from './store/slices/list';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  columns,
  columnsLabels,
  expandableColumns,
  filtersSchema,
  schema,
} from './variables/list';
import formatOutgoingList from './helpers/format-outgoing-list';
import { useLazyGetGeneralLedgerTransactionValuesQuery } from '../transactions/store/services';
import {
  useLazyGetOutgoingListQuery,
  useLazyGetOutgoingListStatusesQuery,
  useExportOutgoingTransactionMutation,
} from './store/services';
import useGetAccountsAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import { clearMfaToken } from '../../store/signature/slice';

import ROUTES from '../../routes';

import cn from './styles.module.css';

let timeout: NodeJS.Timeout;
let timer: NodeJS.Timeout;

const defaultInprogressStatuses = [
  BaseellaStatus.DRAFT,
  BaseellaStatus.NONE,
  BaseellaStatus.NEW,
  BaseellaStatus.HELD,
  BaseellaStatus.SENT,
  BaseellaStatus.PROC,
  BaseellaStatus.ARCH,
  BaseellaStatus.REV,
  BaseellaStatus.AML,
  BaseellaStatus.AML_INFO,
  BaseellaStatus.AML_DONE,
  BaseellaStatus.READY_TO_SEND,
  BaseellaStatus.READY_TO_PSP,
  BaseellaStatus.ACCP_OUT,
  BaseellaStatus.REFUNDED,
];

const OutgoingList = (): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    root: { userId },
    outgoing: {
      list: {
        transactions,
        pagination,
        expandedTransactionId,
        collapseAllRows,
        selectedCount,
        errorsList,
      },
    },
    transactionsDictionaries: {
      paymentTypes,
      currencies,
      activePermissions,
      countries,
      pspPartnerAccounts,
      currenciesList,
    },
    signatures: { mfaToken },
  } = useAppSelector((state) => state);

  const [sendCode, { isLoading: sendCodeInProgress }] = useSendCodeMutation();
  const [validateCode, { isLoading: validateCodeInProgress }] =
    useValidateCodeMutation();
  const [
    createSignature,
    {
      isLoading: createSignatureInProgress,
      isSuccess: createSignatureIsSuccess,
    },
  ] = useBatchCreateTransactionSignatureMutation();

  const [statusFilterState, setStatusFilterState] = useState<
    'in-progress' | 'unsigned' | 'rejected' | 'all' | undefined
  >(undefined);
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [onlyRemoved, setOnlyRemoved] = useState(false);
  const [savedFilters, setSavedFilters] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState('');

  const [unsignedTransactionsList, setUnsignedTransactionsList] = useState<
    string[]
  >([]);

  const [signaturePopupVisible, setSignaturePopupVisible] = useState(false);
  const [errorMessageSignature, setErrorMessageSignature] = useState('');
  const [disabledRowIds, setDisabledRowIds] = useState<string[]>([]);

  const {
    autocompleteList: senderAccounts,
    onSearch: onSearchSenderAccounts,
    onClear,
  } = useGetAccountsAutocompleteList(
    '&isClientAccount=true',
    'fullNumber',
    true,
  );

  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const navigate = useNavigate();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);
  const prevTransactions = usePrevious(transactions);

  const [
    exportTransaction,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportOutgoingTransactionMutation();

  const onExportClick = async () => {
    const exportFilters: Record<string, string[] | string | number> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'cursor') {
          if (item !== 'transferDate') {
            if (
              item === 'consumerStatus' ||
              item === 'baseellaStatus' ||
              item === 'monitoringStatus' ||
              item === 'benificiaryType' ||
              item === 'chargeType' ||
              item === 'id' ||
              item === 'senderUserId' ||
              item === 'recipientUserId' ||
              item === 'currencyId' ||
              item === 'paymentOrderId' ||
              item === 'typeId' ||
              item === 'excludeAccounts'
            ) {
              exportFilters[item] = filters[item].value;
            } else {
              const [filterValue] = filters[item].value;

              exportFilters[item] = filterValue;
            }
          } else if (item === 'transferDate') {
            const transferDate = filters[item].value;

            const [from, to] = transferDate;

            exportFilters.transferDateFrom = from;

            if (to) {
              exportFilters.transferDateTo = to;
            }
          }
        }
      });
    }

    await exportTransaction(exportFilters);
  };

  const [
    getTransactionsList,
    { isLoading, isFetching: outgoingListLoading, isSuccess },
  ] = useLazyGetOutgoingListQuery();
  const [getOutgoingStatuses] = useLazyGetOutgoingListStatusesQuery();

  const [
    getGeneralLedgerTransactionValues,
    { isLoading: glIsLoading, isFetching },
  ] = useLazyGetGeneralLedgerTransactionValuesQuery();

  const getGeneralLedgerAction = (id: string) => {
    dispatch(setExpandedTransactionId(id));
    getGeneralLedgerTransactionValues(id);
  };

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<BaseTransaction>({
      getList: getTransactionsList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const onSendCode = async () => {
    clearTimeout(timer);

    const response = (await sendCode({ mfa: MfaType.TOTP })) as {
      data: { data: MfaToken };
    };

    if (response && response.data && response.data.data) {
      setSignaturePopupVisible(true);

      const expireTime = Math.round(
        // eslint-disable-next-line react-hooks/purity
        new Date(response.data.data.expiresAt).getTime() - Date.now(),
      );

      timer = setTimeout(() => {
        onSendCode();
      }, expireTime);
    }
  };

  const validateCodeAction = async (code: string) => {
    clearTimeout(timer);
    setErrorMessageSignature('');

    if (code) {
      const response = (await validateCode({
        mfaToken: mfaToken as string,
        totpToken: code,
      })) as {
        data?: { data: MfaToken };
        error: { data: { message: string; statusCode: number } };
      };

      if (response && response.data && response.data.data) {
        const createResponse = (await createSignature({
          mfaToken: response.data.data.token,
          transactionId: unsignedTransactionsList,
        })) as {
          error: { data: { message: string }; status: number };
        };

        if (
          createResponse &&
          createResponse.error &&
          createResponse.error.data
        ) {
          if (createResponse.error?.status === 403) {
            setErrorMessageSignature('Access denied');
          } else {
            // eslint-disable-next-line no-lonely-if
            if (!Array.isArray(createResponse.error.data.message)) {
              setErrorMessageSignature(createResponse.error.data.message);
            } else if (Array.isArray(createResponse.error.data.message)) {
              setErrorMessageSignature(
                'Internal error. Transaction wasn`t signed',
              );
            }
          }
        }
      } else if (response && response.error && response.error.data) {
        if (response.error.data.statusCode === 403) {
          setErrorMessageSignature('Access denied');
        } else if (response.error.data.statusCode === 401) {
          setErrorMessageSignature('Incorrect OTP code');
        } else {
          setErrorMessageSignature(response.error.data.message);
        }
      }
    } else {
      setErrorMessageSignature('OTP Code should not be empty');
    }
  };

  const onSavedFilterClick = (id: string, savedFilter: Filters) => {
    setStatusFilterState(undefined);

    const filterValues: Filters = {
      ...savedFilter,
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    onFilter(filterValues);
    setSelectedSavedFilter(id);
  };

  const onInProgressClick = () => {
    setStatusFilterState('in-progress');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: defaultInprogressStatuses,
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');
    onFilter(filterValues);
  };

  const onUnsignedClick = () => {
    setStatusFilterState('unsigned');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: [BaseellaStatus.NOT_SIGNED],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');

    onFilter(filterValues);
  };

  const onRejectedClick = () => {
    setStatusFilterState('rejected');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: [BaseellaStatus.AML_REJ, BaseellaStatus.REJECTED],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');

    onFilter(filterValues);
  };

  const onAllClick = () => {
    setStatusFilterState('all');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: [],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');

    onFilter(filterValues);
  };

  const handleClose = () => {
    setSignaturePopupVisible(false);
    setErrorMessageSignature('');
    dispatch(clearMfaToken());
  };

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'senderAccountNumber') {
      onSearchSenderAccounts(searchAutocomplete);
    }
  };

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStatuses = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getOutgoingStatuses(query);
      await onGetStatuses(query);
    }, 5000);
  };

  const onDuplicate = (id: string) => {
    const currentTransaction = transactions?.find((item) => item.id === id);

    if (currentTransaction) {
      navigate(`${ROUTES.OUTGOING_TRANSACTION_CREATE}${search || ''}`, {
        state: { ...currentTransaction, duplicate: true },
      });
    }
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'outgoing',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        dispatch(setSelectedCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(
    () => () => {
      setTimeout(async () => {
        await onClearTimeout();
      });

      dispatch(clearOutgoingListState());
    },
    [dispatch],
  );

  useEffect(() => {
    setValues('senderAccountNumber', senderAccounts);
    setValues('currencyId', currencies as SelectItemProps[]);
    setValues(
      'paymentType',
      paymentTypes?.map((item) => ({
        name: item.name,
        value: item.paymentType,
      })) || [],
    );
    setValues(
      'pspPartnerAccountNumber',
      pspPartnerAccounts as SelectItemProps[],
    );
    setValues('benificiaryCountry', countries as SelectItemProps[]);
  }, [senderAccounts, currencies, paymentTypes, pspPartnerAccounts, countries]);

  useEffect(() => {
    if (
      selectedCount &&
      selectedCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !transactions &&
      sort &&
      sort.sortProp
    ) {
      let parsed;

      if (search) {
        parsed = queryString.parse(search);
      }

      let filtersValues: any = {
        take: {
          value: [selectedCount?.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      const savedFiltersJSON = localStorage.getItem('outgoing-filters');

      let parsedSavedFilters: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
      }[];

      let defaultSavedFilter;

      if (!Object.keys(filters)?.length) {
        if (savedFiltersJSON) {
          parsedSavedFilters = JSON.parse(savedFiltersJSON);

          if (parsedSavedFilters && parsedSavedFilters?.length) {
            const currentUserParsedFilters = parsedSavedFilters?.filter(
              (item: any) => item.employeeId === userId,
            );

            if (currentUserParsedFilters && currentUserParsedFilters?.length) {
              defaultSavedFilter = currentUserParsedFilters?.find(
                (item) => item.default,
              );
            }
          }
        }

        if (defaultSavedFilter) {
          setSelectedSavedFilter(defaultSavedFilter.id);

          filtersValues = {
            ...filtersValues,
            ...defaultSavedFilter?.value,
          };
        } else {
          filtersValues = {
            ...filtersValues,
            ...filters,
          };
        }
      } else {
        filtersValues = {
          ...filtersValues,
          ...filters,
        };
      }

      if (!filtersValues.baseellaStatus) {
        filtersValues.baseellaStatus = {
          value: defaultInprogressStatuses,
          type: SchemaFilterType.STRING,
        };

        setStatusFilterState('in-progress');
      } else if (filtersValues?.baseellaStatus?.value && !defaultSavedFilter) {
        const sortedDefaultStatusesInProgress = defaultInprogressStatuses.sort(
          (a, b) => a?.localeCompare(b),
        );

        const sortedValues = filtersValues?.baseellaStatus?.value.sort(
          (a: any, b: any) => a?.localeCompare(b),
        );

        if (isEqual(sortedValues, sortedDefaultStatusesInProgress)) {
          setStatusFilterState('in-progress');
        } else if (isEqual(sortedValues, [BaseellaStatus.NOT_SIGNED])) {
          setStatusFilterState('unsigned');
        } else if (
          isEqual(sortedValues, [
            BaseellaStatus.AML_REJ,
            BaseellaStatus.REJECTED,
          ])
        ) {
          setStatusFilterState('rejected');
        } else {
          setStatusFilterState('all');
        }
      }

      if (parsed && parsed.includeRemoved) {
        setIncludeRemoved(true);

        filtersValues.includeRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      if (parsed && parsed.onlyRemoved) {
        setOnlyRemoved(true);

        filtersValues.onlyRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({
        filters: filtersValues,
        sort,
      });
    }
  }, [filters, sort, transactions, selectedCount, userId]);

  useEffect(() => {
    if (selectedSavedFilter && statusFilterState) {
      setStatusFilterState(undefined);
    }
  }, [selectedSavedFilter, statusFilterState]);

  useEffect(() => {
    if (createSignatureIsSuccess) {
      const nextDisabledRowIds = ([] as string[]).concat(disabledRowIds);

      unsignedTransactionsList.forEach((item) => {
        nextDisabledRowIds.push(item);
      });

      setDisabledRowIds(nextDisabledRowIds);

      handleClose();

      if (transactions && transactions.length) {
        const ids = transactions.map((item) => item.id);
        const query = `?id=${ids.join('&id=')}`;

        setTimeout(() => {
          onGetStatuses(query);
        }, 300);
      }
    }
  }, [createSignatureIsSuccess, transactions]);

  useEffect(() => {
    if (transactions) {
      setDisabledRowIds(
        transactions
          ?.filter((item) => item.baseellaStatus !== BaseellaStatus.NOT_SIGNED)
          ?.map((item) => item.id),
      );
    }
  }, [transactions]);

  const formattedData = useMemo(() => {
    const data = formatOutgoingList({
      list: transactions as BaseTransaction[],
      countries,
      currenciesList,
      paymentTypes,
    });

    return data;
  }, [transactions, countries, currenciesList, paymentTypes]);

  useEffect(() => {
    if (transactions && transactions.length) {
      const ids = transactions.map((item) => item.id);
      const query = `?id=${ids.join('&id=')}`;

      onGetStatuses(query);
    }
  }, [transactions]);

  useEffect(() => {
    if (!isEqual(prevTransactions, transactions) && expandedTransactionId) {
      getGeneralLedgerAction(expandedTransactionId);
    }
  }, [prevTransactions, transactions, expandedTransactionId]);

  useEffect(() => {
    const savedFiltersJSON = localStorage.getItem('outgoing-filters');

    if (savedFiltersJSON) {
      const parsed = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const currentEmployeeSavedFilters = parsed?.filter(
          (item: any) => item.employeeId === userId,
        );

        setSavedFilters(currentEmployeeSavedFilters);
      }
    }
  }, [userId]);

  const content: React.JSX.Element = (
    <Table
      title="Outgoing transactions"
      columns={columns}
      data={formattedData || []}
      onSort={(name) => {
        onSort(name);
        onClearTimeout();
      }}
      filters={filters}
      sort={sort}
      filtersComponent={filtersComponent}
      columnsLabels={columnsLabels}
      onFilterChange={(values) => {
        setSelectedSavedFilter('');

        const filterValues: Filters = {
          ...values,
          take: {
            value: [(selectedCount as number)?.toString()],
            type: SchemaFilterType.STRING,
          },
        };

        if (includeRemoved) {
          filterValues.includeRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        if (onlyRemoved) {
          filterValues.onlyRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        onFilter(filterValues);

        if (!values?.baseellaStatus?.value) {
          setStatusFilterState('all');
        }

        if (
          values.baseellaStatus?.value &&
          filters.baseellaStatus?.value &&
          (!isEqual(
            values.baseellaStatus.value,
            filters.baseellaStatus.value,
          ) ||
            selectedSavedFilter)
        ) {
          const sortedDefaultStatusesInProgress =
            defaultInprogressStatuses.sort((a, b) => a?.localeCompare(b));

          const sortedValues = values.baseellaStatus?.value.sort((a, b) =>
            a?.localeCompare(b),
          );

          if (isEqual(sortedValues, sortedDefaultStatusesInProgress)) {
            setStatusFilterState('in-progress');
          } else if (isEqual(sortedValues, [BaseellaStatus.NOT_SIGNED])) {
            setStatusFilterState('unsigned');
          } else if (
            isEqual(sortedValues, [
              BaseellaStatus.AML_REJ,
              BaseellaStatus.REJECTED,
            ])
          ) {
            setStatusFilterState('rejected');
          } else {
            setStatusFilterState('all');
          }
        }
        onClearTimeout();
      }}
      handleSubmit={handleSubmit}
      isLoading={
        isLoading ||
        outgoingListLoading ||
        !selectedCount ||
        transactions === undefined
      }
      take={(selectedCount as number)?.toString()}
      setPage={(data) => {
        const filterValues: Filters = {
          ...filters,
          take: {
            value: [(selectedCount as number)?.toString()],
            type: SchemaFilterType.STRING,
          },
        };

        if (includeRemoved) {
          filterValues.includeRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        if (onlyRemoved) {
          filterValues.onlyRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        setPage({ ...data, filters: filterValues });
        onClearTimeout();
      }}
      pagination={pagination}
      hiddenFiltersTags={statusFilterState !== 'all' ? ['baseellaStatus'] : []}
      hiddedAllFilterTags={Boolean(selectedSavedFilter)}
      filtersContent={
        <div>
          {activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_OUTGOING_LIST,
          ) && (
            <div className={cn.defaultFilters}>
              <button
                type="button"
                className={`${cn.defaultFilterButton} ${
                  statusFilterState === 'in-progress' ? cn.active : ''
                }`}
                onClick={() => onInProgressClick()}
              >
                In progress
              </button>
              <button
                type="button"
                className={`${cn.defaultFilterButton} ${
                  statusFilterState === 'unsigned' ? cn.active : ''
                }`}
                onClick={() => onUnsignedClick()}
              >
                Not signed
              </button>
              <button
                type="button"
                className={`${cn.defaultFilterButton} ${
                  statusFilterState === 'rejected' ? cn.active : ''
                }`}
                onClick={() => onRejectedClick()}
              >
                Rejected
              </button>
              <button
                type="button"
                className={`${cn.defaultFilterButton} ${
                  statusFilterState === 'all' ? cn.active : ''
                }`}
                onClick={() => onAllClick()}
              >
                All
              </button>
            </div>
          )}
        </div>
      }
      topContent={
        <div className={cn.tableTopContent}>
          {activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_OUTGOING_CREATE_ACTION,
          ) && (
            <NavLink
              to={`${ROUTES.OUTGOING_TRANSACTION_CREATE}${search || ''}`}
            >
              <ButtonPrimary
                text="Create transaction"
                size={ButtonSize.SMALL}
                icon={<IconExpand />}
              />
            </NavLink>
          )}
          {unsignedTransactionsList?.length > 0 && (
            <ButtonSecondary
              className={`button-white ${cn.batchSignButton}`}
              text={
                sendCodeInProgress
                  ? 'Loading...'
                  : `Sign transactions (${unsignedTransactionsList.length})`
              }
              size={ButtonSize.SMALL}
              onClick={onSendCode}
            />
          )}
          {activePermissions?.includes(
            PermissionEnum.TRANSACTIONS_OUTGOING_LIST,
          ) && (
            <ButtonPseudo
              text={exportIsLoading ? 'Loading...' : 'Export transactions'}
              icon={<IconDownload />}
              className={`button-white ${cn.exportButton}`}
              size={ButtonSize.SMALL}
              onClick={onExportClick}
            />
          )}
        </div>
      }
      storeKey="outgoing-transactions"
      expandableColumns={expandableColumns}
      error={errorsList || errorList}
      onExpand={getGeneralLedgerAction}
      isLoadingExpandableRow={glIsLoading || isFetching}
      paginationDisabled={outgoingListLoading}
      onClearAutoComplete={() => onClear}
      collapseAllRows={collapseAllRows}
      isFetching={outgoingListLoading}
      onDuplicateClick={(id) => onDuplicate(id)}
      disabledRowIds={disabledRowIds}
      onRowsSelect={(ids) => setUnsignedTransactionsList(ids)}
      clearSelectedItems={createSignatureIsSuccess}
      tableClassName={!transactions?.length ? cn.outgoingTable : ''}
      tableBodyClassName={cn.outgoingTableBody}
      savedFilters={savedFilters}
      setSavedFilters={setSavedFilters}
      savedFiltersKey="outgoing-filters"
      selectedSavedFilter={selectedSavedFilter}
      onSavedFilterClick={onSavedFilterClick}
      setSelectedSavedFilter={setSelectedSavedFilter}
      userId={userId}
      isSuccessExpandableRow={isSuccess}
      transactionTable
      withDuplicateButton
      withVerticalScroll
      expandable
      withSettings
      withScroll
      clickable
    />
  );

  return (
    <div className={cn.outgoingWrapper} ref={scrollRef}>
      {content}
      <SignaturePopup
        signaturePopupVisible={signaturePopupVisible}
        validateCode={validateCodeAction}
        validateCodeInProgress={
          validateCodeInProgress || createSignatureInProgress
        }
        errorMessageSignature={errorMessageSignature}
        onSignatureCancelClick={handleClose}
      />
      <div id="signature-popup" />
    </div>
  );
};

export default OutgoingList;
